"use client";

import { AnimatePresence, motion } from "framer-motion";
import { Folder, FolderPlus, MoreHorizontal, Search } from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dreamWorldProvider from "../DreamWorldProvider";
import ContentModal from "../components/ContentModal";
import PublishContent from "../components/create/PublishContent";
import ContentCard from "../components/dashboard/ContentCard";
import LoadingIndicator from "../components/ui/LoadingIndicator";
import {
  reduxSetAlbums,
  reduxSetLibraryContent,
  selectAlbums,
  selectLibraryContent,
  selectLibraryHasMoreContent,
  selectLibraryLastVisible,
  selectSelectedLibraryItem,
  selectUserGlobalData,
  setSelectedLibraryItem,
  toggleLikeContent,
  updateLibraryHasMoreContent,
  updateLibraryLastVisible,
} from "../store/dreamworldSlice";
import type { AppDispatch } from "../store/store";
import type { Album, ContentType, LibraryItem } from "../types/library";
import { Notification } from "../types/notifications";
import PageLayout from "../components/ui/PageLayout";

const LibraryPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const userGlobalData = useSelector(selectUserGlobalData);
  const hasMoreContent = useSelector(selectLibraryHasMoreContent);
  const libraryContent = useSelector(selectLibraryContent);
  const albums = useSelector(selectAlbums);
  const selectedItem = useSelector(selectSelectedLibraryItem);

  const [publishItem, setPublishItem] = useState<LibraryItem | null>(null);

  const [showCreateAlbum, setShowCreateAlbum] = useState(false);
  const [newAlbumName, setNewAlbumName] = useState("");
  const [showAllAlbums, setShowAllAlbums] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastVisibleLibraryItem = useSelector(selectLibraryLastVisible);

  const loadMoreRef = useRef<HTMLDivElement>(null);
  const getLibraryContent = async () => {
    try {
      if (userGlobalData && libraryContent.length === 0) {
        setLoading(true);
        const lbC = await dreamWorldProvider.getLibraryContent(
          userGlobalData.user.uid
        );
        const albums = await dreamWorldProvider.getAlbums(
          userGlobalData.user.uid
        );
        dispatch(updateLibraryLastVisible(lbC.lastVisible));
        dispatch(reduxSetLibraryContent(lbC.items));
        dispatch(reduxSetAlbums(albums));
      }
    } catch (error) {
      console.error("Error fetching library content:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getLibraryContent();
  }, []);

  const stats = useMemo(() => {
    const totalItems = libraryContent.length;
    const totalFavorites = libraryContent.filter(
      (item) => item.isFavorite
    ).length;
    const totalViews = libraryContent.reduce(
      (sum, item) => sum + item.views,
      0
    );
    const totalLikes = libraryContent.reduce(
      (sum, item) => sum + item.likes,
      0
    );
    const totalAlbums = albums.length;

    const typeBreakdown = libraryContent.reduce(
      (acc, item) => {
        acc[item.type] = (acc[item.type] || 0) + 1;
        return acc;
      },
      {} as Record<ContentType, number>
    );

    return {
      totalItems,
      totalFavorites,
      totalViews,
      totalLikes,
      totalAlbums,
      typeBreakdown,
    };
  }, [libraryContent, albums]);

  const getMoreLibraryContent = async () => {
    try {
      if (
        userGlobalData &&
        hasMoreContent &&
        libraryContent.length > 0 &&
        !loading
      ) {
        setLoading(true);
        const lbC = await dreamWorldProvider.getLibraryContent(
          userGlobalData.user.uid,
          lastVisibleLibraryItem!
        );
        dispatch(reduxSetLibraryContent(lbC.items));
        dispatch(updateLibraryLastVisible(lbC.lastVisible));
        if (lbC.items.length === 0) {
          dispatch(updateLibraryHasMoreContent(false));
        }
      }
    } catch (error) {
      console.error("Error fetching library content:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          hasMoreContent &&
          loading !== true &&
          userGlobalData &&
          libraryContent.length > 0
        ) {
          getMoreLibraryContent();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasMoreContent, loading, userGlobalData, libraryContent]);

  const handleCreateAlbum = async () => {
    if (newAlbumName.trim() && userGlobalData) {
      const newAlbum: Omit<Album, "id" | "itemCount" | "createdAt"> = {
        name: newAlbumName,
        description: "",
        thumbnail: "/api/placeholder/300/300",
        ownerId: userGlobalData.user.uid,
        color: ["purple", "blue", "green", "red", "orange"][
          Math.floor(Math.random() * 5)
        ],
      };
      const newAlbumAllData = await dreamWorldProvider.createAlbum(newAlbum);

      dispatch(reduxSetAlbums([newAlbumAllData]));
      setNewAlbumName("");
      setShowCreateAlbum(false);
    }
  };

  const AlbumCard: React.FC<{ album: Album }> = ({ album }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={`relative p-4 rounded-xl bg-gradient-to-br from-${album.color}-500/10 to-${album.color}-600/5 border border-${album.color}-500/20 cursor-pointer`}
      onClick={() => navigate(`/albums/${album.id}`)}
    >
      <div className="flex items-center gap-3">
        <div
          className={`w-12 h-12 rounded-lg bg-${album.color}-500/20 flex items-center justify-center`}
        >
          <Folder className={`w-6 h-6 text-${album.color}-400`} />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-white">{album.name}</h3>
          <p className="text-sm text-gray-400">{album.itemCount} items</p>
        </div>
        <button className="p-1 hover:bg-gray-700 rounded">
          <MoreHorizontal className="w-4 h-4 text-gray-400" />
        </button>
      </div>
    </motion.div>
  );

  const renderLoading = () => {
    return (
      <div className="bg-gradient-to-br from-slate-950 via-purple-950 to-slate-900 min-h-screen text-white">
        <div className="max-w-screen-2xl mx-auto p-2 sm:p-6">
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            <LoadingIndicator />
          </div>
        </div>
      </div>
    );
  };

  if (loading && libraryContent.length === 0) {
    return renderLoading();
  }

  return (
    <div className="bg-gradient-to-br from-slate-950 via-purple-950 to-slate-900 min-h-screen text-white">
      <PageLayout>
        {/* Header with Stats */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl md:text-5xl font-bold bg-gradient-to-br from-purple-400 to-pink-400 bg-clip-text text-transparent py-2 leading-tight">
                My Library
              </h1>
              <p className="text-gray-400 mt-2 text-sm sm:text-base">
                Manage and explore your AI-generated content
              </p>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowCreateAlbum(true)}
              className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 px-3 py-2 sm:px-4 sm:py-2 rounded-lg transition-colors mt-4 sm:mt-0 text-sm sm:text-base"
            >
              <FolderPlus className="w-4 h-4" />
              New Album
            </motion.button>
          </div>

          <div className="relative mb-8">
            {/* Background blur effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-purple-500/10 rounded-2xl backdrop-blur-sm border border-white/10"></div>

            <div className="relative p-4 sm:p-6">
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3 sm:gap-4">
                <div className="text-center">
                  <div className="text-2xl sm:text-lg font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                    {stats.totalItems}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-300 font-medium">
                    Items
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-lg font-bold bg-gradient-to-r from-red-400 to-pink-400 bg-clip-text text-transparent">
                    {stats.totalFavorites}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-300 font-medium">
                    Favorites
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-lg font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                    {stats.totalViews > 999
                      ? `${(stats.totalViews / 1000).toFixed(1)}K`
                      : stats.totalViews}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-300 font-medium">
                    Views
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-lg font-bold bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                    {stats.totalLikes > 999
                      ? `${(stats.totalLikes / 1000).toFixed(1)}K`
                      : stats.totalLikes}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-300 font-medium">
                    Likes
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-lg font-bold bg-gradient-to-r from-purple-400 to-violet-400 bg-clip-text text-transparent">
                    {stats.totalAlbums}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-300 font-medium">
                    Albums
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-lg font-bold bg-gradient-to-r from-indigo-400 to-blue-400 bg-clip-text text-transparent">
                    {stats.typeBreakdown.Image || 0}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-300 font-medium">
                    Images
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Albums Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg sm:text-xl font-semibold">Albums</h2>
            {albums.length > 3 && !showAllAlbums && (
              <button
                onClick={() => setShowAllAlbums(true)}
                className="px-3 py-1 rounded text-xs sm:text-sm transition-colors text-gray-400 hover:text-white"
              >
                View All
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {loading === true && albums.length === 0 ? (
              <div className="col-span-full">
                <LoadingIndicator />
              </div>
            ) : (
              (showAllAlbums ? albums : albums.slice(0, 3)).map((album) => (
                <AlbumCard key={album.id} album={album} />
              ))
            )}
          </div>
        </motion.div>

        {/* Content Results */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <p className="text-gray-400 text-sm sm:text-base">
              {libraryContent.length}{" "}
              {libraryContent.length === 1 ? "item" : "items"} found
            </p>
          </div>

          {loading === true && libraryContent.length === 0 ? (
            <div className="text-center py-16">Loading...</div>
          ) : libraryContent.length > 0 ? (
            <>
              <div
                className={`gap-0.5 sm:gap-0.5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-5 2xl:grid-cols-5`}
              >
                <AnimatePresence>
                  {libraryContent.map((item, index) => (
                    <motion.div
                      layout
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3, delay: index * 0.02 }}
                      key={item.id}
                    >
                      <ContentCard
                        item={item}
                        onClick={() => dispatch(setSelectedLibraryItem(item))}
                        onLike={(id) => {
                          if (userGlobalData) {
                            dispatch(
                              toggleLikeContent({
                                contentId: id,
                                userId: userGlobalData.user.uid,
                              })
                            );
                            // const notification: Omit<
                            //   Notification,
                            //   "id" | "timestamp"
                            // > = {
                            //   recipientId: userGlobalData.userData.uid,
                            //   senderId: userGlobalData.userData.uid,
                            //   type: "like",
                            //   contentId: item.id,
                            //   read: false,
                            // };
                            // dreamWorldProvider.createNotification(notification);
                          }
                        }}
                        viewMode="grid"
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              <div
                ref={loadMoreRef}
                className="h-20 flex items-center justify-center"
              >
                {loading === true && <LoadingIndicator />}
                {!hasMoreContent && libraryContent.length > 0 && (
                  <div className="text-gray-400 text-sm">
                    All content loaded.
                  </div>
                )}
              </div>
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <div className="w-24 h-24 mx-auto mb-6 bg-gray-800 rounded-full flex items-center justify-center">
                <Search className="w-8 h-8 text-gray-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">No content found</h3>
              <p className="text-gray-400">
                Try adjusting your search or filters
              </p>
            </motion.div>
          )}
        </motion.div>

        {/* Modals */}
        <AnimatePresence>
          {showCreateAlbum && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowCreateAlbum(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-gray-800 rounded-xl p-6 w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
              >
                <h2 className="text-xl font-semibold mb-4">Create New Album</h2>
                <input
                  type="text"
                  placeholder="Album name"
                  value={newAlbumName}
                  onChange={(e) => setNewAlbumName(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 mb-4 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  autoFocus
                />
                <div className="flex gap-3">
                  <button
                    onClick={handleCreateAlbum}
                    disabled={!newAlbumName.trim()}
                    className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed px-4 py-2 rounded-lg transition-colors"
                  >
                    Create Album
                  </button>
                  <button
                    onClick={() => setShowCreateAlbum(false)}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {publishItem && (
          <PublishContent
            item={publishItem}
            onClose={() => setPublishItem(null)}
          />
        )}

        <AnimatePresence>
          {selectedItem && (
            <ContentModal
              contentId={selectedItem.id}
              source="library"
              onClose={() => dispatch(setSelectedLibraryItem(null))}
              onLike={(id) => {
                if (userGlobalData) {
                  dispatch(
                    toggleLikeContent({
                      contentId: id,
                      userId: userGlobalData.user.uid,
                    })
                  );
                  const notification: Omit<Notification, "id" | "timestamp"> = {
                    recipientId: userGlobalData.userData.uid,
                    senderId: selectedItem.ownerId,
                    type: "like",
                    contentId: selectedItem.id,
                    read: false,
                  };
                  dreamWorldProvider.createNotification(notification);
                }
              }}
            />
          )}
        </AnimatePresence>
      </PageLayout>
    </div>
  );
};

export default LibraryPage;
